import React, { useState, useEffect } from "react";
import "./SignUpScreen.css";
import { useDispatch } from "react-redux";
import { setUserDetails, setUserId, setUserName } from "../../redux/userSlice";
import { useNavigate } from "react-router-dom";
import CompanyApprovalModal from "./Modal/Modal";
import { FaUser, FaEnvelope, FaLock, FaPhone, FaMapMarkerAlt, FaBuilding, FaCalendarAlt, FaVenusMars, FaCheckCircle, FaSpinner } from "react-icons/fa";
import Autocomplete from "../../Utils/AutoComplete";
import "../../Utils/AutoComplete.css";
import VerifyCompanyModal from "./verifyModal/VerifyModal";
import { apiUrl, otpApiUrl } from "../../config/api.js";
import { devLog } from "../../config/devLog.js";

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    companyName: "",
    email: "",
    corporateEmail:'',
    password: "",
    mobile: "",
    location: "",
    isNri: false,
    agreeTnc: false,
    otp:""
  });
  
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState("");
  const [showOTP,setShowOTP] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalActive, setIsModalActive] = useState(false)
  const [showVerifyModal,setShowVerifyModal] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  


  const [companies, setCompanies] = useState({}); // store {name: emailDomain}
  const [companyQuery, setCompanyQuery] = useState("");
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null); // { name, domain }
  const [showRequestCompany, setShowRequestCompany] = useState(false);
  const [requestCompany, setRequestCompany] = useState({ name: "", domain: "" });
  const [corpLocalPart, setCorpLocalPart] = useState("");

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(apiUrl("getCompaniesData")); // your API endpoint
        if (!res.ok) {
          throw new Error("Failed to fetch companies");
        }
        const data = await res.json();
        devLog("[signup] companies loaded", { count: Array.isArray(data) ? data.length : Object.keys(data || {}).length });
        setCompanies(data);
      } catch (error) {
        devLog("Error fetching companies:", error?.message);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const all = Object.entries(companies || {})
      .map(([name, domain]) => ({
        name: String(name || "").trim(),
        domain: String(domain || "").trim().toLowerCase().replace(/^@/, ""),
      }))
      .filter((c) => c.name && c.domain);

    const q = String(companyQuery || "").trim().toLowerCase();
    if (!q) {
      setCompanySuggestions(all.slice(0, 12));
      return;
    }
    setCompanySuggestions(
      all.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 12)
    );
  }, [companies, companyQuery]);

  useEffect(() => {
    if (!selectedCompany?.domain) {
      setFormData((prev) => ({ ...prev, corporateEmail: "" }));
      return;
    }
    const local = String(corpLocalPart || "").trim().toLowerCase();
    const domain = String(selectedCompany.domain || "").trim().toLowerCase().replace(/^@/, "");
    setFormData((prev) => ({ ...prev, corporateEmail: local ? `${local}@${domain}` : "" }));
  }, [corpLocalPart, selectedCompany]);




  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    devLog(
      name === "password"
        ? { name, type, checked, value: "[redacted]" }
        : { name, value, type, checked }
    );
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Calculate password strength
    if (name === "password") {
      let strength = 0;
      if (value.length >= 6) strength += 1;
      if (value.length >= 8) strength += 1;
      if (/[A-Z]/.test(value)) strength += 1;
      if (/[a-z]/.test(value)) strength += 1;
      if (/[0-9]/.test(value)) strength += 1;
      if (/[^A-Za-z0-9]/.test(value)) strength += 1;
      setPasswordStrength(strength);
    }
  };

  /** Accepts `company.com`, `@company.com`, or `user@company.com` → `company.com` */
  function normalizeOrgEmailDomainInput(raw) {
    let s = String(raw || "").trim().toLowerCase();
    if (!s) return "";
    s = s.replace(/^@+/, "");
    const at = s.lastIndexOf("@");
    if (at >= 0) s = s.slice(at + 1).trim();
    return s.replace(/^@+/, "");
  }

  const validateForm = () => {
    let newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.dob) newErrors.dob = "Date of Birth is required";
    if (!showRequestCompany) {
      if (!selectedCompany?.name) newErrors.companyName = "Please select your company/college from the list";
    } else {
      if (!requestCompany.name.trim()) newErrors.companyName = "Company/College name is required";
      const domainNorm = normalizeOrgEmailDomainInput(requestCompany.domain);
      if (!domainNorm) newErrors.companyDomain = "Company/College email domain is required";
      else if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domainNorm)) {
        newErrors.companyDomain =
          "Enter the email domain only (example: company.com), not a full email address";
      }
    }
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Valid Email is required";
    if (!formData.password.trim() || formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    if (!formData.mobile.trim() || !/^\d{10}$/.test(formData.mobile))
      newErrors.mobile = "Valid 10-digit Mobile Number is required";
    if (!formData.location.trim()) newErrors.location = "Preferred Location is required";
    if (!formData.agreeTnc) newErrors.agreeTnc = "You must agree to T&C";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };




    const SignUp = async (userDetails) =>{
    setLoading(true);
    setShowError("");
    devLog("[signup] company check", { company: userDetails.companyName });
    if(companies.hasOwnProperty(userDetails.companyName.toLowerCase())){
      devLog("[signup] company recognized in directory");
    }
    const response = await fetch(apiUrl("signup"), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        
        body: JSON.stringify({
          "firstName": userDetails.firstName,
          "lastName": userDetails.lastName,
          "gender": userDetails.gender,
          "dob": userDetails.dob,
          "companyName": userDetails.companyName,
          "email": userDetails.email,
          "corporate_email": userDetails.corporateEmail,
          "mobileNumber": userDetails.mobile,
          "preferredLocation": "USA_"+userDetails.location,
          "password": userDetails.password,
          "isNRI": 'Yes',
          "otp": String(userDetails.otp ?? "").trim(),
          "isActive": true,
          "is_verified": isVerified
        }),
        
      })

      const res = await response.json();
      devLog("[signup] signup response", {
        message: res?.message,
        userId: res?.userId,
        errorCode: res?.errorCode,
        errno: res?.errno,
      });
      if(res.message==="Email already registered"){
        setShowError("Email is already registered try logging in")
        setLoading(false);
      }
      else if(res.message==="Invalid OTP"){
        setShowError("Invalid OTP")
        setLoading(false);
      }
      else if (response.ok && res.userId) {
        if (res.token) {
          localStorage.setItem("token", res.token);
        }
        localStorage.setItem("userID", String(res.userId));
        localStorage.setItem("userId", String(res.userId));

      dispatch(setUserId(res.userId))
      dispatch(setUserName(res.name))
      const profileRes = await fetch(apiUrl("getUser?"+res.userId))
      const profileData = await profileRes.json()
      dispatch(setUserDetails({...profileData, userId: profileData.id, name: profileData.first_name+" "+profileData.last_name, userEmail: profileData.email, company: profileData.companyName?profileData.companyName:"", isVerified: profileData.is_verified, corporateEmail: profileData.corporate_email }))
      
        navigate('/home');


      
      setLoading(false);
    //   navigation.reset({
    //     index: 0,
    //     routes: [{ name: 'Home' }]
    // });
  } else {
        const ref =
          res.errorCode != null && res.errorCode !== ""
            ? ` (${String(res.errorCode)})`
            : "";
        setShowError((res.message || "Signup failed. Please try again.") + ref);
        setLoading(false);
      }
      
  }

  const getOTP = async () =>{
    const response = await fetch(otpApiUrl("getOTP"),{method:"POST", headers: {
      'Content-Type': 'application/json',
    },body:JSON.stringify({email:formData.email})})
    await response.json();
    // if (Platform.OS === 'android') {
    //   ToastAndroid.show("OTP sent !", ToastAndroid.SHORT)
    // } else {
    //   AlertIOS.alert("OTP sent !");
    // }
    devLog("[signup] getOTP response", { ok: response.ok });
    setLoading(false);
    setShowOTP(true)
    setTimeout(() => {
      // setResendOtp(true)
    }, 5000);
  }

  const handleResendOtp = async () => {
    setShowError("");
    setFormData((prev) => ({ ...prev, otp: "" }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.otp;
      return next;
    });
    setLoading(true);
    try {
      const response = await fetch(otpApiUrl("getOTP"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      await response.json().catch(() => ({}));
      devLog("[signup] resend getOTP", { ok: response.ok });
      if (!response.ok) {
        setShowError("Could not resend the code. Please try again.");
        setLoading(false);
        return;
      }
      setShowOTP(true);
    } catch (e) {
      devLog("[signup] resend getOTP failed", e?.message);
      setShowError("Could not resend the code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /*
    const sendUpdate = async (email) =>{
      const emailContent = {
        email: email,
        subject: "Your Account is Under Review",
        text: `Hello,

      Thank you for signing up with us. We noticed that the company name you provided does not match our directory.

      Your account is currently under review by our admin team and will be enabled shortly. You will receive an email notification once your account has been approved.

      Thank you for your patience.

      Best regards,
         Team Corporate NRI`, 
        html: `
        <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
          <p>Hello,</p>
          
          <p>Thank you for signing up with us. We noticed that the company name you provided does not match our directory.</p>
          
          <p><strong>Your account is currently under review</strong> by our admin team and will be enabled shortly. You will receive an email notification once your account has been approved.</p>
          
          <p>Thank you for your patience.</p>
          
          <p>Best regards,<br>
          <strong>Team Corporate NRI</strong></p>
        </div>`
      };

    const response = await fetch(otpApiUrl("sendMail"),{method:"POST", headers: {
      'Content-Type': 'application/json',
    },body:JSON.stringify(emailContent)})
    await response.json();
    // if (Platform.OS === 'android') {
    //   ToastAndroid.show("OTP sent !", ToastAndroid.SHORT)
    // } else {
    //   AlertIOS.alert("OTP sent !");
    // }
    devLog("[signup] sendMail (sendUpdate)", { ok: response.ok });
    setLoading(false);
    // setShowOTP(true)
    setTimeout(() => {
      // setResendOtp(true)
    }, 5000);
  }


      const sendUpdatetoservice = async (formData) =>{
      const emailContent = {
        email: 'support@corporatenri.com',
        subject: "New User account for Review",
        text: `Hello,

        There is a new user account under review, here are the details

        Name - ${formData.firstName}, ${formData.lastName},

        Company - ${formData.companyName},

        Email - ${formData.email}

      Best regards,
         Team Corporate NRI`, 
        html: `
        <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
          <p>Hello,</p>
          
          <p>There is a new user account under review, here are the details.</p>

            <p>Name - ${formData.firstName}, ${formData.lastName}</p>

            <p>Company - ${formData.companyName}</p>

            <p>Email - ${formData.email}</p>
          
          <p>Best regards,<br>
          <strong>Team Corporate NRI</strong></p>
        </div>`
      };
    const response = await fetch(otpApiUrl("sendMail"),{method:"POST", headers: {
      'Content-Type': 'application/json',
    },body:JSON.stringify(emailContent)})
    await response.json();
    devLog("[signup] sendMail (sendUpdatetoservice)", { ok: response.ok });
    setLoading(false);
    // setShowOTP(true)
    setTimeout(() => {
      // setResendOtp(true)
    }, 5000);
  }
*/

  const submitCompanyRequest = async () => {
    const companyName = requestCompany.name.trim();
    const emailDomain = normalizeOrgEmailDomainInput(requestCompany.domain);

    const res = await fetch(apiUrl("requestCompany"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyName,
        emailDomain,
        requesterEmail: formData.email,
        requesterFirstName: formData.firstName,
        requesterLastName: formData.lastName,
      }),
    });

    const payload = await res.json().catch(() => ({}));
    const outcome = payload.outcome;
    /** Treat known outcomes as success even if API Gateway reports a non-2xx status (misconfiguration). */
    const successOutcomes = ["duplicate_same_requester", "domain_already_pending", "created"];
    if (!successOutcomes.includes(outcome) && !res.ok) {
      throw new Error(payload?.message || "Failed to submit company request");
    }

    if (outcome === "duplicate_same_requester") {
      devLog("[signup] company request duplicate (same email + domain), no email sent");
      return;
    }

    const newRequestUserEmail = {
      email: formData.email,
      subject: "Company/College verification in progress",
      text: `Hello ${formData.firstName || ""},

We received your request to add/verify your organization:
- Name: ${companyName}
- Email domain: ${emailDomain}

You can log in now with your current email and use the platform.
Once your company/college is verified by our admin team, you will receive an email to verify your corporate email from your Profile (Update Profile).

Best regards,
Team Corporate NRI`,
      html: `<div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
<p>Hello ${formData.firstName || ""},</p>
<p>We received your request to add/verify your organization:</p>
<ul>
  <li><strong>Name:</strong> ${companyName}</li>
  <li><strong>Email domain:</strong> ${emailDomain}</li>
</ul>
<p>You can log in now with your current email and use the platform.</p>
<p>Once your company/college is verified by our admin team, you will receive an email to verify your corporate email from your Profile (Update Profile).</p>
<p>Best regards,<br/><strong>Team Corporate NRI</strong></p>
</div>`,
    };

    const domainPendingUserEmail = {
      email: formData.email,
      subject: "Company domain verification in progress",
      text: `Hello ${formData.firstName || ""},

The email domain ${emailDomain} is already under review for addition to our directory. No new request was created.

You can log in with your current email and use the platform. When the domain is approved, you can verify your corporate email from your Profile (Update Profile).

Best regards,
Team Corporate NRI`,
      html: `<div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
<p>Hello ${formData.firstName || ""},</p>
<p>The email domain <strong>${emailDomain}</strong> is already under review for addition to our directory. <strong>No new request was created.</strong></p>
<p>You can log in with your current email and use the platform. When the domain is approved, you can verify your corporate email from your Profile (Update Profile).</p>
<p>Best regards,<br/><strong>Team Corporate NRI</strong></p>
</div>`,
    };

    const adminEmailContent = {
      email: "support@corporatenri.com",
      subject: "New company/college request (signup)",
      text: `Hello,

A user requested to add/verify a company/college:

Requester: ${formData.firstName} ${formData.lastName}
Requester email: ${formData.email}

Company/College: ${companyName}
Domain: ${emailDomain}

Best regards,
Team Corporate NRI`,
      html: `<div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
<p>Hello,</p>
<p>A user requested to add/verify a company/college:</p>
<p><strong>Requester:</strong> ${formData.firstName} ${formData.lastName}<br/>
<strong>Requester email:</strong> ${formData.email}</p>
<p><strong>Company/College:</strong> ${companyName}<br/>
<strong>Domain:</strong> ${emailDomain}</p>
<p>Best regards,<br/><strong>Team Corporate NRI</strong></p>
</div>`,
    };

    try {
      if (outcome === "domain_already_pending") {
        await fetch(otpApiUrl("sendMail"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(domainPendingUserEmail),
        });
        return;
      }

      await fetch(otpApiUrl("sendMail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newRequestUserEmail),
      });
      await fetch(otpApiUrl("sendMail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(adminEmailContent),
      });
    } catch (e) {
      devLog("[signup] sendMail failed (non-blocking)", e?.message);
    }
  };

  const onSubmit = async (e) => {
     e.preventDefault();

    if (validateForm()) {
    if(!showOTP){
    setLoading(true);
      try {
        if (showRequestCompany) {
          await submitCompanyRequest();
          setSelectedCompany(null);
          setCorpLocalPart("");
          setIsVerified(false);
          setFormData((prev) => ({
            ...prev,
            companyName: requestCompany.name.trim(),
            corporateEmail: "",
          }));
        } else if (selectedCompany?.name) {
          setFormData((prev) => ({ ...prev, companyName: selectedCompany.name }));
        }
      } catch (err) {
        setShowError(err?.message || "Failed to submit company request");
        setLoading(false);
        return;
      }
      const response = await fetch(apiUrl("verifyUser"),{method:"POST", headers: {
        'Content-Type': 'application/json',
      },body:JSON.stringify({email:formData.email, mobileNumber: formData.mobile})})
      const data = await response.json();
      devLog("[signup] verifyUser", { message: data?.message });
      if(data.message==="Email already registered"){
        setShowError("Email is already registered try logging in")
        setLoading(false)
      }
      else if(data.message==="Mobile Number already registered"){
        setShowError("Mobile number is already registered try logging in with that account")
        setLoading(false)
      }
      else if(data.message === "Success"){
        getOTP();
      }
      
    }
    else {
      SignUp(formData)
    }
  }
    
  };

  


const handleBlur = (e) => {
  const { name, value } = e.target;
  let error = "";

  if (name === "email") {
    if (!value) {
      error = "Email is required";
    }
    // else if( !value.endsWith(companies[formData.companyName.toLowerCase()]) ) {
    //   error = "Please provide your college Or Company Email";
    // }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = "Please enter a valid email address";
    }
  }

  setErrors((prevErrors) => ({
    ...prevErrors,
    [name]: error,
  }));
};


  const handleLocationSelect = (value) => {
    setFormData(prev => ({...prev, location: value}));
  }

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 2) return { text: "Weak", color: "#e53e3e" };
    if (passwordStrength <= 4) return { text: "Medium", color: "#f6ad55" };
    return { text: "Strong", color: "#38a169" };
  };

  const getPasswordStrengthWidth = () => {
    return `${(passwordStrength / 6) * 100}%`;
  };

  return (
    <div className="signup-container">
      <CompanyApprovalModal isOpen={isModalActive} onClose={()=>{setIsModalActive(false); navigate('/')}} />
       {showVerifyModal&& (
          <VerifyCompanyModal
            isOpen={showVerifyModal}
            onClose={(verified) => {
              setShowVerifyModal(false);
              setIsVerified(verified);
            }}
            message=""
            corporateEmail={formData.corporateEmail}
            signupMode
          />
        )}

      
      <div className="signup-layout">
        {/* Left Side - Trust Building Content */}
        <div className="signup-left">
          <div className="trust-content">
            <div className="trust-header">
              <div className="trust-icon">
                <FaUser className="icon" />
              </div>
              <h1>Join Corporate NRI</h1>
              <p>Connect with fellow USA NRIs and access exclusive services</p>
            </div>

            <div className="trust-stats">
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Active USA NRIs</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">200+</div>
                <div className="stat-label">Verified Listings</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">50+</div>
                <div className="stat-label">Connections Made</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Free Forever</div>
              </div>
            </div>

            <div className="trust-features">
              <div className="feature-item">
                <FaCheckCircle className="feature-icon" />
                <span>Verified USA NRI Community</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="feature-icon" />
                <span>Safe & Secure Platform</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="feature-icon" />
                <span>24/7 Community Support</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="feature-icon" />
                <span>No Hidden Fees</span>
              </div>
            </div>

            <div className="trust-testimonial">
              <div className="testimonial-content">
                <p>"Great platform to connect with fellow NRIs! Found helpful advice about rental properties from others who've been there."</p>
                <div className="testimonial-author">
                  <strong>Rajesh Kumar</strong>
                  <span>Software Engineer, Dallas</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="signup-right">
          <div className="signup-form-wrapper">
            <div className="form-header">
              <h2>Create Your Account</h2>
              <p>Join our community of USA NRIs</p>
            </div>

            <form onSubmit={onSubmit} className="signup-form">
              <div className="form-row">
                <div className="input-group">
                  <label>
                    <FaUser className="input-icon" />
                    First Name
                  </label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={formData.firstName} 
                    onChange={handleChange}
                    placeholder="Enter your first name"
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                
                <div className="input-group">
                  <label>
                    <FaUser className="input-icon" />
                    Last Name
                  </label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={formData.lastName} 
                    onChange={handleChange}
                    placeholder="Enter your last name"
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label>
                    <FaVenusMars className="input-icon" />
                    Gender
                  </label>
                  <select 
                    name="gender" 
                    value={formData.gender} 
                    onChange={handleChange}
                    className={errors.gender ? 'error' : ''}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>
                
                <div className="input-group">
                  <label>
                    <FaCalendarAlt className="input-icon" />
                    Date of Birth
                  </label>
                  <input 
                    type="date" 
                    name="dob" 
                    value={formData.dob}  
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]} 
                    onChange={handleChange}
                    className={errors.dob ? 'error' : ''}
                  />
                  {errors.dob && <span className="error-message">{errors.dob}</span>}
                </div>
              </div>

              <div className="input-group">
                <label>
                  <FaBuilding className="input-icon" />
                  Company/College Name
                </label>
                {!showRequestCompany ? (
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={companyQuery}
                      onChange={(e) => {
                        setCompanyQuery(e.target.value);
                        setSelectedCompany(null);
                        setCorpLocalPart("");
                        setIsVerified(false);
                      }}
                      placeholder="Search and select your company/college"
                      className={errors.companyName ? "error" : ""}
                      autoComplete="off"
                    />
                    {companyQuery?.trim()?.length > 0 &&
                      companySuggestions?.length > 0 &&
                      (!selectedCompany ||
                        selectedCompany.name.toLowerCase() !== companyQuery.trim().toLowerCase()) && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "100%",
                          zIndex: 20,
                          background: "#fff",
                          border: "1px solid #e2e8f0",
                          borderTop: "none",
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {companySuggestions.map((c) => (
                          <button
                            key={`${c.name}-${c.domain}`}
                            type="button"
                            onClick={() => {
                              setSelectedCompany({ name: c.name, domain: c.domain });
                              setCompanyQuery(c.name);
                              setFormData((prev) => ({ ...prev, companyName: c.name }));
                              setShowRequestCompany(false);
                              setRequestCompany({ name: "", domain: "" });
                              setIsVerified(false);
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              padding: "10px 12px",
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ fontWeight: 600 }}>{c.name}</div>
                            <div style={{ fontSize: 12, color: "#4a5568" }}>{c.domain}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={requestCompany.name}
                    onChange={(e) => setRequestCompany((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Enter your company or college name"
                    className={errors.companyName ? "error" : ""}
                  />
                )}
                {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                {!showRequestCompany && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => {
                        setShowRequestCompany(true);
                        setSelectedCompany(null);
                        setCompanyQuery("");
                        setCorpLocalPart("");
                        setIsVerified(false);
                        setFormData((prev) => ({ ...prev, corporateEmail: "", companyName: "" }));
                      }}
                    >
                      Can't find your company/college? Request to add it
                    </button>
                  </div>
                )}
              </div>

              {showRequestCompany && (
                <div className="input-group">
                  <label>
                    <FaEnvelope className="input-icon" />
                    Company/College Email Domain
                  </label>
                  <input
                    type="text"
                    value={requestCompany.domain}
                    onChange={(e) => setRequestCompany((p) => ({ ...p, domain: e.target.value }))}
                    placeholder="example: company.com"
                    className={errors.companyDomain ? "error" : ""}
                  />
                  {errors.companyDomain && <span className="error-message">{errors.companyDomain}</span>}
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => {
                        setShowRequestCompany(false);
                        setRequestCompany({ name: "", domain: "" });
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.companyDomain;
                          return next;
                        });
                      }}
                    >
                      Back to company search
                    </button>
                  </div>
                </div>
              )}

              <div className="input-group">
                <label>
                  <FaEnvelope className="input-icon" />
                  Email Address
                </label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email}  
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  placeholder="Enter your email address"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>


              <div className="input-group">
                <div style={{display:"flex", justifyContent:"space-between"}}>
                <label>
                  <FaEnvelope className="input-icon" />
                  Corporate Email Address
                </label>
                {isVerified&&
                <FaCheckCircle style={{color:'green', marginTop:"auto", marginBottom:"10px"}}/>}
                </div>
                {!showRequestCompany && selectedCompany?.domain ? (
                  <>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="text"
                        value={corpLocalPart}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (/^[a-z0-9._%+-]*$/i.test(v)) {
                            setCorpLocalPart(v);
                            setIsVerified(false);
                          }
                        }}
                        placeholder="yourname"
                        style={{ flex: 1 }}
                      />
                      <div style={{ whiteSpace: "nowrap", color: "#4a5568", fontWeight: 600 }}>
                        @{normalizeOrgEmailDomainInput(selectedCompany.domain)}
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        padding: "6px 2px 0px 2px",
                        color: "#4a5568",
                        fontWeight: 600,
                        borderBottom: "1px dashed #e2e8f0",
                      }}
                    >
                      {formData.corporateEmail || "yourname@company.com"}
                    </div>
                  </>
                ) : (
                  <input 
                    type="email" 
                    name="corporateEmail" 
                    value={formData.corporateEmail}  
                    onChange={handleChange} 
                    onBlur={handleBlur}
                    placeholder={showRequestCompany ? "Corporate email can be verified after your company is added" : "Select a company/college to auto-fill domain"}
                    className={errors.corporateEmail ? 'error' : ''}
                    disabled={showRequestCompany}
                  />
                )}
                {errors.corporateEmail && <span className="error-message">{errors.corporateEmail}</span>}
                {!showRequestCompany && formData?.corporateEmail?.length > 0 && (
                  <p className="verifyOTP"  onClick={()=>{setShowVerifyModal(true)}}>
                    verify by OTP
                  </p>
                )}
              </div>

              <div className="input-group">
                <label>
                  <FaLock className="input-icon" />
                  Password
                </label>
                <div className="password-input-container">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    name="password" 
                    value={formData.password} 
                    onChange={handleChange}
                    placeholder="Create a strong password"
                    className={errors.password ? 'error' : ''}
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div 
                        className="strength-fill" 
                        style={{ 
                          width: getPasswordStrengthWidth(),
                          backgroundColor: getPasswordStrengthText().color 
                        }}
                      ></div>
                    </div>
                    <span 
                      className="strength-text"
                      style={{ color: getPasswordStrengthText().color }}
                    >
                      {getPasswordStrengthText().text}
                    </span>
                  </div>
                )}
                
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              <div className="input-group">
                <label>
                  <FaPhone className="input-icon" />
                  Mobile Number
                </label>
                <input 
                  type="tel" 
                  name="mobile" 
                  value={formData.mobile} 
                  onChange={handleChange}
                  placeholder="Enter your 10-digit mobile number"
                  className={errors.mobile ? 'error' : ''}
                />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>

              <div className="input-group">
                <label>
                  <FaMapMarkerAlt className="input-icon" />
                  Preferred State (USA)
                </label>
                <div className="location-autocomplete">
                  <Autocomplete 
                    inputValue={formData.location}
                    setInputValue={handleLocationSelect}
                  />
                </div>
                {errors.location && <span className="error-message">{errors.location}</span>}
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="isNri" 
                    checked={formData.isNri} 
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">I am a Non-Resident Indian (NRI)</span>
                </label>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="agreeTnc" 
                    checked={formData.agreeTnc} 
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    I agree to the <button type="button" className="terms-link" onClick={() => {/* Handle terms click */}}>Terms and Conditions</button>
                  </span>
                </label>
                {errors.agreeTnc && <span className="error-message">{errors.agreeTnc}</span>}
              </div>

              {/* OTP Section */}
              {showOTP && (
                <div className="otp-section">
                  <div className="otp-header">
                    <h3>Email Verification</h3>
                    <p>We've sent a verification code to {formData.email}</p>
                  </div>
                  
                  <div className="input-group">
                    <label>
                      <FaCheckCircle className="input-icon" />
                      Enter Verification Code
                    </label>
                    <input 
                      type="number" 
                      name="otp" 
                      value={formData.otp} 
                      onChange={handleChange}
                      placeholder="Enter 6-digit code"
                      className={errors.otp ? 'error' : ''}
                    />
                    {errors.otp && <span className="error-message">{errors.otp}</span>}
                  </div>
                  
                  <div className="otp-actions">
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleResendOtp}
                      disabled={loading}
                    >
                      Resend Code
                    </button>
                    <span className="otp-timer">Code expires in 5:00</span>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {showError && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  <span>{showError}</span>
                </div>
              )}

              {/* Submit Button */}
              <div className="form-actions">
                <button 
                  type="submit" 
                  className={`submit-button ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="spinner" />
                      Processing...
                    </>
                  ) : (
                    showOTP ? "Complete Registration" : "Create Account"
                  )}
                </button>
                
                <div className="login-link">
                  <span>Already have an account?</span>
                  <button 
                    type="button" 
                    className="login-btn"
                    onClick={() => navigate('/login')}
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
